export enum ActionTypes {
  sendEmail = 'sendEmail',
  stopRecipe = 'stopRecipe',
  startRecipe = 'startRecipe',
  deleteRecipe = 'deleteRecipe',
  stopFlow = 'stopFlow',
  stopAllFlows = 'stopAllFlows',
  startFlow = 'startFlow',
  deleteConnection = 'deleteConnection',
  deleteCanvasApp = 'deleteCanvasApp',
  deleteFlow = 'deleteFlow',
  deleteEnvironment = 'deleteEnvironment',
  quarantineApplication = 'quarantineApplication',
  unquarantineApplication = 'unquarantineApplication',
  sendWebhook = 'sendWebhook',
  stopAllRecipes = 'stopAllRecipes',
  addOwnerToCanvasApp = 'addOwnerToCanvasApp',
  addOwnerToCustomConnector = 'addOwnerToCustomConnector',
  addOwnerToFlow = 'addOwnerToFlow',
  addOwnerToEnvironment = 'addOwnerToEnvironment',
  addOwnerToConnection = 'addOwnerToConnection',
  addOwnerToCopilot = 'addOwnerToCopilot',
  exemptViolation = 'exemptViolation',
  resetViolationStatus = 'resetViolationStatus',
  wait = 'wait',
  secureFlowSteps = 'secureFlowSteps',
  setCopilotAuthentication = 'setCopilotAuthentication',
  removeAccessFromCopilot = 'removeAccessFromCopilot',
  addLabel = 'addLabel',
  removeAccess = 'removeAccess',
  isolateFlowResource = 'isolateFlowResource',
  isolateCopilotResource = 'isolateCopilotResource',
  deleteAgent = 'deleteAgent',
  deletePowerBIDataset = 'deletePowerBIDataset',
  deletePowerBIReport = 'deletePowerBIReport',

  setActionStatus = 'setActionStatus', // exists only on FE to combine enable and disable actions into one action to properly display in Playbooks
  enableAgentAction = 'enableAgentAction',
  disableAgentAction = 'disableAgentAction',

  setAgentActionAuthenticationMode = 'setAgentActionAuthenticationMode',
  setUserContext = 'setUserContext', // exists only on FE to specify Authentication mode actions to properly display in Playbooks
  setAuthorContext = 'setAuthorContext', // exists only on FE to specify Authentication mode actions to properly display in Playbooks

  setAgentActionConfirmationMode = 'setAgentActionConfirmationMode',
  setStrictConfirmationMode = 'setStrictConfirmationMode', // exists only on FE to specify Confirmation mode actions to properly display in Playbooks
  setDisabledConfirmationMode = 'setDisabledConfirmationMode', // exists only on FE to specify Confirmation mode actions to properly display in Playbooks

  setAgentWebBrowsing = 'setAgentWebBrowsing',
  enableWebBrowsing = 'enableWebBrowsing', // exists only on FE to specify Web Browsing actions to properly display in Playbooks
  disableWebBrowsing = 'disableWebBrowsing', // exists only on FE to specify Web Browsing actions to properly display in Playbooks

  setEnvironmentAudit = 'setEnvironmentAudit',
  enableEnvironmentAudit = 'enableEnvironmentAudit', // exists only on FE to specify Environment Audit actions to properly display in Playbooks
  disableEnvironmentAudit = 'disableEnvironmentAudit', // exists only on FE to specify Environment Audit actions to properly display in Playbooks

  deleteAgentThread = 'deleteAgentThread',
  isolateGpt = 'isolateGpt',
  sendToSoc = 'sendToSoc',
  isolateWorkspace = 'isolateWorkspace',
  setAllUsersAccessWorkspace = 'setAllUsersAccessWorkspace',
}

export const AddOwnerActions = [
  ActionTypes.addOwnerToCanvasApp,
  ActionTypes.addOwnerToConnection,
  ActionTypes.addOwnerToCustomConnector,
  ActionTypes.addOwnerToEnvironment,
  ActionTypes.addOwnerToFlow,
  ActionTypes.addOwnerToCopilot,
];

export const RemovePermissionsActions = [ActionTypes.removeAccessFromCopilot];

export const RemoveAccessActions = [ActionTypes.removeAccess];

export const IsolateResourceActions = [
  ActionTypes.isolateCopilotResource,
  ActionTypes.isolateFlowResource,
  ActionTypes.isolateGpt,
  ActionTypes.isolateWorkspace,
];

export const DeleteAgentAction = [ActionTypes.deleteAgent];

export const DeletePowerBIResourceActions = [ActionTypes.deletePowerBIDataset, ActionTypes.deletePowerBIReport];

export const SetAllUsersAccessWorkspaceActions = [ActionTypes.setAllUsersAccessWorkspace];

/**
 * Converts camelCase enum value to Title Case display name
 * Examples:
 * - sendEmail -> Send Email
 * - addOwnerToFlow -> Add Co-Owner to Flow
 * - isolateGpt -> Isolate GPT
 */
function generateActionDisplayName(actionType: string): string {
  // Split camelCase into words
  const words = actionType
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ');

  // Apply special transformations
  const transformed = words.map((word, index) => {
    const lowerWord = word.toLowerCase();

    // Handle acronyms that should stay uppercase
    if (lowerWord === 'gpt' || lowerWord === 'bi') {
      return word.toUpperCase();
    }

    // Handle "Owner" -> "Co-Owner" in "addOwner" context
    if (lowerWord === 'owner' && index > 0 && words[index - 1].toLowerCase() === 'add') {
      return 'Co-Owner';
    }

    // Capitalize first letter of each word
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return transformed.join(' ');
}

/**
 * Manual overrides for specific action type display names.
 * Use this only when the automatic generation doesn't produce the desired result.
 */
const DISPLAY_NAME_OVERRIDES: Partial<Record<ActionTypes, string>> = {
  // Example: [ActionTypes.someAction]: 'Custom Display Name',
};

/**
 * Dynamically generated display names for all action types.
 * This map is automatically created from the ActionTypes enum values.
 * To override a specific display name, add it to the DISPLAY_NAME_OVERRIDES object above.
 */
export const ActionTypeDisplayNames: Record<ActionTypes, string> = Object.values(ActionTypes).reduce(
  (acc, actionType) => {
    acc[actionType] = DISPLAY_NAME_OVERRIDES[actionType] ?? generateActionDisplayName(actionType);
    return acc;
  },
  {} as Record<ActionTypes, string>
);
